import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { addMinutes, endOfDay, format, parse, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import FormDateInput from '../../../components/form/FormDateInput';
import { daysOfWeek, forwardingTypeOptions } from '../../../utils/common';

import {
  audioPromptsAllOptionGet,
  didDetailsGetById,
  didProcessOptionGet,
  didsAdd,
  didsUpdate,
  didUserOptionGet,
  ivrOptionsGet,
  queuesOptionGet
} from '../../../store/slices/tenantSlices/incomingSlice';
import {
  aiPhoneNumberOptionsGet,
  callexAiAgentOptionsGet,
  outboundOptionGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { toZonedTime } from 'date-fns-tz';
import { useTimezone } from '../../../store/slices/commonSlice';

const AddEditDidModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);

  const isCallexAiAgent = process.env.REACT_APP_CALLEX_AI_AGENT === 'true';

  const timeCondition = {
    from_day: '',
    to_day: '',
    from_time: addMinutes(startOfDay(today), 1),
    to_time: endOfDay(today),
    forwarding_type: '',
    forwarding_options: ''
  };

  const holidayInitialValue = {
    date: today,
    forwarding_type: '',
    forwarding_options: ''
  };

  const [processOptions, setProcessOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [queuesOption, setQueuesOption] = useState([]);
  const [audioPromptOption, setAudioPromptOption] = useState([]);
  const [outboundRoutesOptions, setOutboundRoutesOptions] = useState([]);
  const [aiPhoneNumberOption, setAiPhoneNumberOption] = useState([]);
  const [ivrOption, setIvrOption] = useState([]);

  const [timeConditionDetails, setTimeConditionDetails] = useState([{ ...timeCondition }]);
  const [holidayDetails, setHolidayDetails] = useState([{ ...holidayInitialValue }]);

  const initialValues = {
    number: '',
    status: 'true',
    time_condition: 'no',
    forwarding_type: '',
    forwarding_options: '',
    failover_forwarding_type: '',
    failover_forwarding_option: '',
    outboundroutes_id: '',
    time_conditions: [],
    holidays: []
  };

  const validationSchema = Yup.object({
    number: Yup.string().required('number is required'),
    forwarding_type: Yup.string().required('forwarding type is required'),
    forwarding_options: Yup.string().required('forwarding options is required'),
    outboundroutes_id: Yup.string().required('outbound routes is required')
  });

  const getAudioPromptOption = async () => {
    try {
      const response = await dispatch(audioPromptsAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setAudioPromptOption(options);
      } else {
        setAudioPromptOption([]);
      }
    } catch (error) {
      setAudioPromptOption([]);
      console.log('error', error);
    }
  };

  const getQueuesOption = async () => {
    try {
      const response = await dispatch(queuesOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setQueuesOption(options);
      } else {
        setQueuesOption([]);
      }
    } catch (error) {
      setQueuesOption([]);
      console.log('error', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(didProcessOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(didUserOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: String(val.id)
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  const getOutboundRoutesOption = async () => {
    try {
      const response = await dispatch(outboundOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setOutboundRoutesOptions(options);
      } else {
        setOutboundRoutesOptions([]);
      }
    } catch (error) {
      setOutboundRoutesOptions([]);
      console.log('error', error);
    }
  };

  const getIvrOption = async () => {
    try {
      const response = await dispatch(ivrOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setIvrOption(options);
      } else {
        setIvrOption([]);
      }
    } catch (error) {
      setIvrOption([]);
      console.log('error', error);
    }
  };

  const getAiPhoneNumberOption = async () => {
    try {
      const response = await dispatch(aiPhoneNumberOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.phone_number
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  const getAIAgentOption = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data?.agents.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getIvrOption();
    getOutboundRoutesOption();
    if (isCallexAiAgent) {
      getAIAgentOption();
    } else {
      getAiPhoneNumberOption();
    }
    getAudioPromptOption();
    getQueuesOption();
    getProcessOption();
    getUserOption();
  }, [isCallexAiAgent]);

  const onSubmit = async () => {
    const formatDate = (date) => format(new Date(date), 'yyyy-MM-dd');
    const formatTime = (date) => format(new Date(date), 'HH:mm');

    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };

        if (values.time_condition === 'yes') {
          payload['time_conditions'] = timeConditionDetails.map((condition) => ({
            ...condition,
            from_time: formatTime(condition.from_time),
            to_time: formatTime(condition.to_time)
          }));

          payload['holidays'] = holidayDetails.map((holiday) => ({
            ...holiday,
            date: formatDate(holiday.date)
          }));
        } else {
          payload['time_conditions'] = [];
          payload['holidays'] = [];
        }

        const response = await dispatch(didsUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };

        if (values.time_condition === 'yes') {
          payload['time_conditions'] = timeConditionDetails.map((condition) => ({
            ...condition,
            from_time: formatTime(condition.from_time),
            to_time: formatTime(condition.to_time)
          }));

          payload['holidays'] = holidayDetails.map((holiday) => ({
            ...holiday,
            date: formatDate(holiday.date)
          }));
        } else {
          payload['time_conditions'] = [];
          payload['holidays'] = [];
        }

        const response = await dispatch(didsAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const forwardingSelectOptions = [
    'transfer_to_process',
    'transfer_to_queue',
    'transfer_to_playback',
    'transfer_to_agent',
    'transfer_to_ivr',
    'transfer_to_ai_number'
  ];

  const getForwardingOptions = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return processOptions;
    }
    if (forwarding_type === 'transfer_to_queue') {
      return queuesOption;
    }
    if (forwarding_type === 'transfer_to_playback') {
      return audioPromptOption;
    }
    if (forwarding_type === 'transfer_to_agent') {
      return userOptions;
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return ivrOption;
    }
    if (forwarding_type === 'transfer_to_ai_number') {
      return aiPhoneNumberOption;
    }
    return [];
  };

  const getForwardingLabel = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return 'Process';
    }
    if (forwarding_type === 'transfer_to_queue') {
      return 'Queue';
    }
    if (forwarding_type === 'transfer_to_playback') {
      return 'Playback';
    }
    if (forwarding_type === 'transfer_to_agent') {
      return 'User';
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return 'IVR';
    }
    if (forwarding_type === 'transfer_to_ai_number') {
      return isCallexAiAgent ? 'AI Agent' : 'AI Number';
    }
    return '';
  };

  const handleTimeConditionChange = (index, event) => {
    const { name, value } = event.target;
    const updatedTimeCondition = [...timeConditionDetails];
    updatedTimeCondition[index] = { ...updatedTimeCondition[index], [name]: value };
    if (name === 'forwarding_type') {
      updatedTimeCondition[index] = {
        ...updatedTimeCondition[index],
        forwarding_options: value === 'transfer_to_hangup' ? 'Hangup' : ''
      };
    }
    setTimeConditionDetails(updatedTimeCondition);
  };

  const handleAddTimeCondition = () => {
    setTimeConditionDetails([...timeConditionDetails, { ...timeCondition }]);
  };

  const handleDeleteTimeCondition = (index) => {
    const updatedTimeCondition = timeConditionDetails.filter((_, i) => i !== index);
    setTimeConditionDetails(updatedTimeCondition);
  };

  const handleHolidayChange = (index, event) => {
    const { name, value } = event.target;
    const updatedHoliday = [...holidayDetails];
    updatedHoliday[index] = { ...updatedHoliday[index], [name]: value };
    if (name === 'forwarding_type') {
      updatedHoliday[index] = {
        ...updatedHoliday[index],
        forwarding_options: value === 'transfer_to_hangup' ? 'Hangup' : ''
      };
    }
    setHolidayDetails(updatedHoliday);
  };

  const handleAddHoliday = () => {
    setHolidayDetails([...holidayDetails, { ...holidayInitialValue }]);
  };

  const handleDeleteHoliday = (index) => {
    const updatedHoliday = holidayDetails.filter((_, i) => i !== index);
    setHolidayDetails(updatedHoliday);
  };

  const fetchDidDetailsRecord = async () => {
    try {
      const response = await dispatch(didDetailsGetById(editRecord?.id)).unwrap();
      setValues({
        ...response,
        status: String(response.status),
        time_condition: response?.time_condition === true ? 'yes' : 'no'
      });
      if (response?.time_condition === true) {
        const formatDate = (date) => parse(date, 'yyyy-MM-dd', today);
        const formatTime = (date) => parse(date, 'HH:mm', today);
        const time_conditions = (response?.time_conditions || []).map((condition) => ({
          ...condition,
          from_time: formatTime(condition.from_time),
          to_time: formatTime(condition.to_time)
        }));
        const holidays = (response?.holidays || []).map((holiday) => ({
          ...holiday,
          date: formatDate(holiday.date)
        }));
        setHolidayDetails([...holidays]);
        setTimeConditionDetails([...time_conditions]);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchDidDetailsRecord();
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit DIDs' : 'Add DIDs'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="dids" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="number"
                label="Number"
                placeholder="Enter Number"
                value={values.number}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                trueName="Yes"
                falseName="No"
                name="time_condition"
                label="Time Condition"
                value={String(values.time_condition === 'yes')}
                onChange={() => {
                  setFieldValue('time_condition', values.time_condition === 'yes' ? 'no' : 'yes');
                  setTimeConditionDetails([{ ...timeCondition }]);
                  setHolidayDetails([{ ...holidayInitialValue }]);
                }}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="outboundroutes_id"
                label="Outbound Routes"
                placeholder="Select Outbound Routes"
                value={values.outboundroutes_id}
                options={outboundRoutesOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="forwarding_type"
                label="Forwarding Type"
                placeholder="Select Forwarding Type"
                value={values.forwarding_type}
                options={forwardingTypeOptions}
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value === 'transfer_to_hangup') {
                    setFieldValue('forwarding_options', 'Hangup');
                  } else {
                    setFieldValue('forwarding_options', '');
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            {values.forwarding_type !== '' ? (
              <Col lg="3">
                {forwardingSelectOptions.includes(values.forwarding_type) ? (
                  <FormSelect
                    name="forwarding_options"
                    label={getForwardingLabel(values.forwarding_type)}
                    placeholder={`Select ${getForwardingLabel(values.forwarding_type)}`}
                    value={values.forwarding_options}
                    options={getForwardingOptions(values.forwarding_type)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                ) : values.forwarding_type === 'transfer_to_call_forward' ? (
                  <FormInput
                    name="forwarding_options"
                    label="Call Forward (PSTN)"
                    placeholder="Enter Call Forward"
                    value={values.forwarding_options}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                ) : values.forwarding_type === 'transfer_to_hangup' ? (
                  <FormInput
                    name="forwarding_options"
                    label="Hangup"
                    placeholder="Enter Hangup"
                    value={values.forwarding_options}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={true}
                  />
                ) : null}
              </Col>
            ) : null}
            <Col lg="3">
              <FormSelect
                name="failover_forwarding_type"
                label="Failover Forwarding Type"
                placeholder="Select Forwarding Type"
                value={values.failover_forwarding_type}
                options={forwardingTypeOptions}
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value === 'transfer_to_hangup') {
                    setFieldValue('failover_forwarding_option', 'Hangup');
                  } else {
                    setFieldValue('failover_forwarding_option', '');
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            {values.failover_forwarding_type !== '' ? (
              <Col lg="3">
                {forwardingSelectOptions.includes(values.failover_forwarding_type) ? (
                  <FormSelect
                    name="failover_forwarding_option"
                    label={`Failover ${getForwardingLabel(values.failover_forwarding_type)}`}
                    placeholder={`Select Failover ${getForwardingLabel(values.failover_forwarding_type)}`}
                    value={values.failover_forwarding_option}
                    options={getForwardingOptions(values.failover_forwarding_type)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                ) : values.failover_forwarding_type === 'transfer_to_call_forward' ? (
                  <FormInput
                    name="failover_forwarding_option"
                    label="Failover Call Forward (PSTN)"
                    placeholder="Enter Call Forward"
                    value={values.failover_forwarding_option}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                ) : values.failover_forwarding_type === 'transfer_to_hangup' ? (
                  <FormInput
                    name="failover_forwarding_option"
                    label="Failover Hangup"
                    placeholder="Enter Hangup"
                    value={values.failover_forwarding_option}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={true}
                  />
                ) : null}
              </Col>
            ) : null}
          </Row>
          {values.time_condition === 'yes' ? (
            <React.Fragment>
              <div className="w-100 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center mt-4">
                <div>Time Conditions</div>
                {timeConditionDetails?.length === 0 ? (
                  <span
                    className="cursor-pointer"
                    title="Add Time Conditions"
                    onClick={handleAddTimeCondition}>
                    <Icon.PlusCircle />
                  </span>
                ) : null}
              </div>
              {timeConditionDetails?.map((val, index, array) => {
                return (
                  <Row className="pt-4" key={index}>
                    <Col lg="3">
                      <FormSelect
                        name="from_day"
                        label="From Day"
                        placeholder="Select From Day"
                        value={val.from_day}
                        options={
                          val.to_day
                            ? daysOfWeek.slice(
                              0,
                              daysOfWeek.findIndex((day) => day.value === val.to_day) + 1
                            )
                            : daysOfWeek
                        }
                        onChange={(e) => handleTimeConditionChange(index, e)}
                      />
                    </Col>
                    <Col lg="3">
                      <FormSelect
                        name="to_day"
                        label="To Day"
                        placeholder="Select To Day"
                        value={val.to_day}
                        options={
                          val.from_day
                            ? daysOfWeek.slice(
                              daysOfWeek.findIndex((day) => day.value === val.from_day)
                            )
                            : daysOfWeek
                        }
                        onChange={(e) => handleTimeConditionChange(index, e)}
                      />
                    </Col>
                    <Col lg="3">
                      <FormDateInput
                        dateFormat="HH:mm"
                        timeFormat="HH:mm"
                        label="From Time (HH:MM)"
                        showTimeSelect={true}
                        name="from_time"
                        value={val.from_time}
                        showTimeSelectOnly={true}
                        onChange={(e) => handleTimeConditionChange(index, e)}
                        timeIntervals="1"
                        maxTime={
                          val.to_time ? new Date(val.to_time).setSeconds(0, 0) : endOfDay(today)
                        }
                        minTime={addMinutes(startOfDay(today), 1)}
                      />
                    </Col>
                    <Col lg="3">
                      <FormDateInput
                        dateFormat="HH:mm"
                        timeFormat="HH:mm"
                        label="To Time (HH:MM)"
                        showTimeSelect={true}
                        name="to_time"
                        value={val.to_time}
                        showTimeSelectOnly={true}
                        onChange={(e) => handleTimeConditionChange(index, e)}
                        timeIntervals="1"
                        maxTime={endOfDay(today)}
                        minTime={
                          val.from_time
                            ? new Date(val.from_time).setSeconds(0, 0)
                            : addMinutes(startOfDay(today), 1)
                        }
                      />
                    </Col>

                    <Col lg="3">
                      <FormSelect
                        name="forwarding_type"
                        label="Forwarding Type"
                        placeholder="Select Forwarding Type"
                        value={val.forwarding_type}
                        options={forwardingTypeOptions}
                        onChange={(e) => handleTimeConditionChange(index, e)}
                      />
                    </Col>
                    {val.forwarding_type !== '' ? (
                      <Col lg="3">
                        {forwardingSelectOptions.includes(val.forwarding_type) ? (
                          <FormSelect
                            name="forwarding_options"
                            label={getForwardingLabel(val.forwarding_type)}
                            placeholder={`Select ${getForwardingLabel(val.forwarding_type)}`}
                            value={val.forwarding_options}
                            options={getForwardingOptions(val.forwarding_type)}
                            onChange={(e) => handleTimeConditionChange(index, e)}
                          />
                        ) : val.forwarding_type === 'transfer_to_call_forward' ? (
                          <FormInput
                            name="forwarding_options"
                            label="Call Forward (PSTN)"
                            placeholder="Enter Call Forward"
                            value={val.forwarding_options}
                            onChange={(e) => handleTimeConditionChange(index, e)}
                          />
                        ) : val.forwarding_type === 'transfer_to_hangup' ? (
                          <FormInput
                            name="forwarding_options"
                            label="Hangup"
                            placeholder="Enter Hangup"
                            value={val.forwarding_options}
                            onChange={(e) => handleTimeConditionChange(index, e)}
                            disabled={true}
                          />
                        ) : null}
                      </Col>
                    ) : null}
                    <Col lg="3" className="d-flex align-items-center gap-4 pt-2">
                      <Icon.Trash
                        size={20}
                        onClick={() => {
                          handleDeleteTimeCondition(index);
                        }}
                        className="cursor-pointer text-danger"
                      />
                      {array?.length - 1 === index ? (
                        <Icon.Plus
                          className="cursor-pointer text-info"
                          size={20}
                          onClick={handleAddTimeCondition}
                        />
                      ) : null}
                    </Col>
                  </Row>
                );
              })}

              <div className="w-100 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center mt-4">
                <div>Holidays</div>
                {holidayDetails?.length === 0 ? (
                  <span className="cursor-pointer" title="Add Holidays" onClick={handleAddHoliday}>
                    <Icon.PlusCircle />
                  </span>
                ) : null}
              </div>
              {holidayDetails?.map((val, index, array) => {
                return (
                  <Row className="pt-4" key={index}>
                    <Col lg="3">
                      <FormDateInput
                        dateFormat="yyyy-MM-dd"
                        label="Date"
                        name="date"
                        value={val.date}
                        onChange={(e) => handleHolidayChange(index, e)}
                      />
                    </Col>
                    <Col lg="3">
                      <FormSelect
                        name="forwarding_type"
                        label="Forwarding Type"
                        placeholder="Select Forwarding Type"
                        value={val.forwarding_type}
                        options={forwardingTypeOptions}
                        onChange={(e) => handleHolidayChange(index, e)}
                      />
                    </Col>
                    {val.forwarding_type !== '' ? (
                      <Col lg="3">
                        {forwardingSelectOptions.includes(val.forwarding_type) ? (
                          <FormSelect
                            name="forwarding_options"
                            label={getForwardingLabel(val.forwarding_type)}
                            placeholder={`Select ${getForwardingLabel(val.forwarding_type)}`}
                            value={val.forwarding_options}
                            options={getForwardingOptions(val.forwarding_type)}
                            onChange={(e) => handleHolidayChange(index, e)}
                          />
                        ) : val.forwarding_type === 'transfer_to_call_forward' ? (
                          <FormInput
                            name="forwarding_options"
                            label="Call Forward (PSTN)"
                            placeholder="Enter Call Forward"
                            value={val.forwarding_options}
                            onChange={(e) => handleHolidayChange(index, e)}
                          />
                        ) : val.forwarding_type === 'transfer_to_hangup' ? (
                          <FormInput
                            name="forwarding_options"
                            label="Hangup"
                            placeholder="Enter Hangup"
                            value={val.forwarding_options}
                            onChange={(e) => handleHolidayChange(index, e)}
                            disabled={true}
                          />
                        ) : null}
                      </Col>
                    ) : null}
                    <Col lg="3" className="d-flex align-items-center gap-4 pt-2">
                      <Icon.Trash
                        size={20}
                        onClick={() => {
                          handleDeleteHoliday(index);
                        }}
                        className="cursor-pointer text-danger"
                      />
                      {array?.length - 1 === index ? (
                        <Icon.Plus
                          className="cursor-pointer text-info"
                          size={20}
                          onClick={handleAddHoliday}
                        />
                      ) : null}
                    </Col>
                  </Row>
                );
              })}
            </React.Fragment>
          ) : null}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="dids" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditDidModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditDidModal;
